import { useCallback, useState } from 'react'
import {
  deleteSunoPromptPool,
  getSunoPromptPools,
  updateSunoPromptPool,
  type SunoPromptPool
} from '../../../api/suno'
import type { PromptPoolEditorState } from './PromptManagement'

type UsePromptPoolManagementInput = {
  refreshPrompts: () => Promise<void>
  toast: (message: string) => void
}

export const usePromptPoolManagement = ({
  refreshPrompts,
  toast
}: UsePromptPoolManagementInput) => {
  const [promptPoolEditor, setPromptPoolEditor] = useState<PromptPoolEditorState | null>(null)
  const [promptPoolSaving, setPromptPoolSaving] = useState(false)
  const [expandedPromptPoolIds, setExpandedPromptPoolIds] = useState<Set<string>>(() => new Set())
  const [expandedPromptPoolAnchorPromptIds, setExpandedPromptPoolAnchorPromptIds] = useState<Record<string, string | undefined>>({})
  const [promptPoolDetailsById, setPromptPoolDetailsById] = useState<Record<string, SunoPromptPool | undefined>>({})
  const [promptPoolLoadingIds, setPromptPoolLoadingIds] = useState<Set<string>>(() => new Set())
  const [promptPoolErrorsById, setPromptPoolErrorsById] = useState<Record<string, string | undefined>>({})

  const loadPromptPoolDetails = useCallback(async (poolId: string): Promise<SunoPromptPool | null> => {
    setPromptPoolLoadingIds((current) => {
      const next = new Set(current)
      next.add(poolId)
      return next
    })
    setPromptPoolErrorsById((current) => ({ ...current, [poolId]: undefined }))

    try {
      const pools = await getSunoPromptPools()
      const pool = pools.find((item) => item.id === poolId) ?? null

      setPromptPoolDetailsById((current) => ({ ...current, [poolId]: pool ?? undefined }))

      if (!pool) {
        setPromptPoolErrorsById((current) => ({ ...current, [poolId]: 'Prompt pool not found.' }))
      }

      return pool
    } catch (requestError) {
      setPromptPoolErrorsById((current) => ({
        ...current,
        [poolId]: requestError instanceof Error ? requestError.message : 'Unable to load prompt pool.'
      }))
      return null
    } finally {
      setPromptPoolLoadingIds((current) => {
        const next = new Set(current)
        next.delete(poolId)
        return next
      })
    }
  }, [])

  const openPromptPoolEditor = useCallback(async (poolId: string): Promise<void> => {
    const pool = promptPoolDetailsById[poolId] ?? await loadPromptPoolDetails(poolId)

    if (!pool) {
      toast('Prompt pool not found.')
      return
    }

    setPromptPoolEditor({
      description: pool.description ?? '',
      name: pool.name,
      pool
    })
  }, [loadPromptPoolDetails, promptPoolDetailsById, toast])

  const savePromptPool = useCallback(async (input: PromptPoolEditorState): Promise<void> => {
    const name = input.name.trim()

    if (!name) {
      return
    }

    setPromptPoolSaving(true)

    try {
      const updated = await updateSunoPromptPool({
        canonicalPromptId: input.pool.canonical_suno_prompt_id,
        description: input.description,
        id: input.pool.id,
        name,
        promptIds: input.pool.variants.map((variant) => variant.prompt.id)
      })

      setPromptPoolDetailsById((current) => ({ ...current, [updated.id]: updated }))
      setPromptPoolEditor(null)
      await refreshPrompts()
      toast('Prompt pool updated.')
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to update prompt pool.')
    } finally {
      setPromptPoolSaving(false)
    }
  }, [refreshPrompts, toast])

  const togglePromptPoolExpansion = useCallback((poolId: string, anchorPromptId: string): void => {
    const isExpanded = expandedPromptPoolIds.has(poolId) && expandedPromptPoolAnchorPromptIds[poolId] === anchorPromptId

    setExpandedPromptPoolIds((current) => {
      const next = new Set(current)
      if (isExpanded) {
        next.delete(poolId)
      } else {
        next.add(poolId)
      }
      return next
    })
    setExpandedPromptPoolAnchorPromptIds((current) => ({
      ...current,
      [poolId]: isExpanded ? undefined : anchorPromptId
    }))

    if (!isExpanded && !promptPoolDetailsById[poolId] && !promptPoolLoadingIds.has(poolId)) {
      void loadPromptPoolDetails(poolId)
    }
  }, [expandedPromptPoolAnchorPromptIds, expandedPromptPoolIds, loadPromptPoolDetails, promptPoolDetailsById, promptPoolLoadingIds])

  const openPromptPoolExpansion = useCallback((poolId: string, anchorPromptId: string): void => {
    setExpandedPromptPoolIds((current) => {
      const next = new Set(current)
      next.add(poolId)
      return next
    })
    setExpandedPromptPoolAnchorPromptIds((current) => ({
      ...current,
      [poolId]: anchorPromptId
    }))
    void loadPromptPoolDetails(poolId)
  }, [loadPromptPoolDetails])

  const refreshExpandedPromptPool = useCallback(async (poolId: string): Promise<void> => {
    await loadPromptPoolDetails(poolId)
    await refreshPrompts()
  }, [loadPromptPoolDetails, refreshPrompts])

  const setPromptPoolCanonical = useCallback(async (pool: SunoPromptPool, promptId: string): Promise<void> => {
    try {
      const updated = await updateSunoPromptPool({
        canonicalPromptId: promptId,
        description: pool.description,
        id: pool.id,
        name: pool.name,
        promptIds: pool.variants.map((variant) => variant.prompt.id)
      })
      setPromptPoolDetailsById((current) => ({ ...current, [pool.id]: updated }))
      await refreshPrompts()
      toast('Prompt pool updated.')
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to update prompt pool.')
    }
  }, [refreshPrompts, toast])

  const removePromptPoolVariant = useCallback(async (pool: SunoPromptPool, promptId: string): Promise<void> => {
    if (pool.variants.length <= 2) {
      toast('A prompt pool must keep at least two variants.')
      return
    }

    const remaining = pool.variants.filter((variant) => variant.prompt.id !== promptId)
    const canonicalPromptId = pool.canonical_suno_prompt_id === promptId
      ? remaining[0]!.prompt.id
      : pool.canonical_suno_prompt_id

    try {
      const updated = await updateSunoPromptPool({
        canonicalPromptId,
        description: pool.description,
        id: pool.id,
        name: pool.name,
        promptIds: remaining.map((variant) => variant.prompt.id)
      })
      setPromptPoolDetailsById((current) => ({ ...current, [pool.id]: updated }))
      await refreshPrompts()
      toast('Prompt variant removed from pool.')
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to update prompt pool.')
    }
  }, [refreshPrompts, toast])

  const dissolvePromptPool = useCallback(async (poolId: string): Promise<void> => {
    try {
      await deleteSunoPromptPool(poolId)
      setExpandedPromptPoolIds((current) => {
        const next = new Set(current)
        next.delete(poolId)
        return next
      })
      setExpandedPromptPoolAnchorPromptIds((current) => ({
        ...current,
        [poolId]: undefined
      }))
      setPromptPoolDetailsById((current) => {
        const { [poolId]: _removed, ...remaining } = current
        return remaining
      })
      await refreshPrompts()
      toast('Prompt pool dissolved.')
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to dissolve prompt pool.')
    }
  }, [refreshPrompts, toast])

  return {
    dissolvePromptPool,
    expandedPromptPoolAnchorPromptIds,
    expandedPromptPoolIds,
    openPromptPoolEditor,
    openPromptPoolExpansion,
    promptPoolDetailsById,
    promptPoolEditor,
    promptPoolErrorsById,
    promptPoolSaving,
    refreshExpandedPromptPool,
    removePromptPoolVariant,
    savePromptPool,
    setPromptPoolCanonical,
    setPromptPoolEditor,
    togglePromptPoolExpansion
  }
}
